import { useNavigate, useSearch } from "@tanstack/react-router";

import { FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { applyFilters, parseFilters, type FilterValues } from "@/lib/filters";

import { WINDOWS, type FunnelSummary, type WindowDays } from "./api";
import { funnelName } from "./format";
import { COHORTS, type CohortDays, type OutcomeCut } from "./stats-api";

/**
 * The window and funnel controls, kept in the URL so "the split test for
 * precision-dosing over 90 days" is a link a person can paste. The controls
 * are the shell-owned `FilterBar` (I-7); this module draws no select of its own.
 */
const WINDOW_SPEC: FilterSpec = {
  name: "days",
  label: "Window",
  kind: "choice",
  options: WINDOWS.map((value) => ({ value, label: `${value} days` })),
};

export const DEFAULT_DAYS: WindowDays = 30;

export function parseDays(value: unknown): WindowDays {
  return value === "7" || value === "90" ? (Number(value) as WindowDays) : DEFAULT_DAYS;
}

export type Controls = {
  days: WindowDays;
  funnel: string | null;
  values: FilterValues;
  onChange: (next: FilterValues) => void;
};

export function useControls(): Controls {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const values = parseFilters(search);
  const funnel = typeof values.funnel === "string" && values.funnel ? values.funnel : null;
  return {
    days: parseDays(values.days),
    funnel,
    values,
    onChange: (next) => {
      void navigate({ to: ".", search: applyFilters(search, next), replace: true });
    },
  };
}

export function WindowControl({ controls }: { controls: Controls }) {
  return (
    <FilterBar
      layout="segmented"
      filters={[WINDOW_SPEC]}
      values={{ ...controls.values, days: String(controls.days) }}
      onChange={controls.onChange}
    />
  );
}

/** The `funnel` URL value that means every funnel, on the tabs that offer it (GATE-368 FN-1). */
export const ALL_FUNNELS = "all";

/** A funnel the drop-down can name; the Worker's page list carries no visitor count, so it is optional. */
export type FunnelChoice = Pick<FunnelSummary, "funnel_slug"> & { new_visitors?: number };

/** The funnel with the most new visitors in the window (ties go to the first the Worker listed), or `null` with none. */
export function busiestFunnel(funnels: FunnelChoice[]): string | null {
  let best: FunnelChoice | null = null;
  for (const f of funnels) if (best === null || (f.new_visitors ?? 0) > (best.new_visitors ?? 0)) best = f;
  return best?.funnel_slug ?? null;
}

/**
 * The funnel a tab reads, from the shared `funnel` URL key (GATE-368 FN-1). A tab that offers All
 * (Pages, Email, Sources) opens on All; a tab that does not (Split test, Outcomes) opens on the
 * busiest funnel, and reads "all" carried over from another tab as no choice. A slug the window
 * no longer lists falls back the same way, so a stale link never asks for an unknown funnel.
 */
export function selectedFunnel(value: string | null, funnels: FunnelChoice[], allowAll: boolean): string | null {
  if (value && value !== ALL_FUNNELS && funnels.some((f) => f.funnel_slug === value)) return value;
  if (allowAll) return ALL_FUNNELS;
  return busiestFunnel(funnels);
}

export function funnelLabel(slug: string, allLabel = "All funnels"): string {
  return slug === ALL_FUNNELS ? allLabel : funnelName(slug);
}

/**
 * The funnel the reader chose, or `null` when the tab picked it (All, or the busiest funnel). Only a
 * reader's own choice shows as a set filter, so "Clear 1 filter" never appears with nothing to clear.
 */
export function chosenFunnel(controls: Pick<Controls, "funnel">, selected: string): string | null {
  return controls.funnel !== null && controls.funnel !== ALL_FUNNELS && controls.funnel === selected ? selected : null;
}

/** The select's empty choice on a tab with no All: names the busiest funnel it stands for, whatever is picked now. */
export function mostVisitedLabel(funnels: FunnelChoice[]): string {
  const busiest = busiestFunnel(funnels);
  return busiest === null ? "Most visited" : `Most visited: ${funnelName(busiest)}`;
}

/**
 * The funnel drop-down beside the window chips: one select, because the funnel list grows with
 * every campaign (FN-1). With `allowAll` the empty choice is the All label ("All funnels", or the
 * tab's own wording); without it the empty choice names the busiest funnel in the window.
 */
export function FunnelControls({
  controls,
  funnels,
  selected,
  allowAll = false,
  allLabel = "All funnels",
}: {
  controls: Controls;
  funnels: FunnelChoice[];
  selected: string;
  allowAll?: boolean;
  allLabel?: string;
}) {
  const funnelSpec: FilterSpec = {
    name: "funnel",
    label: "Funnel",
    kind: "choice",
    anyLabel: allowAll ? allLabel : mostVisitedLabel(funnels),
    options: funnels.map((f) => ({ value: f.funnel_slug, label: funnelName(f.funnel_slug) })),
  };
  const funnelValue = chosenFunnel(controls, selected);
  return (
    <div className="flex flex-wrap items-end gap-4">
      <FilterBar
        layout="segmented"
        filters={[WINDOW_SPEC]}
        values={{ ...controls.values, days: String(controls.days) }}
        onChange={controls.onChange}
      />
      <FilterBar
        layout="inline"
        filters={[funnelSpec]}
        values={{ ...controls.values, funnel: funnelValue }}
        onChange={(next) => controls.onChange({ ...next, funnel: next.funnel ?? null })}
      />
    </div>
  );
}

// The Outcomes tab's own controls (C9 R4). The cohort window is its own URL key
// (`cohort`) so it never changes the event window (`days`) the other tabs share.

export const DEFAULT_COHORT: CohortDays = 365;
export const OUTCOME_CUTS: { value: OutcomeCut; label: string }[] = [
  { value: "variant", label: "Variant" },
  { value: "source", label: "Source" },
  { value: "goal", label: "Goal" },
  { value: "product", label: "Product" },
];

export function parseCohort(value: unknown): CohortDays {
  const n = Number(value);
  return (COHORTS as readonly number[]).includes(n) ? (n as CohortDays) : DEFAULT_COHORT;
}

export function parseCut(value: unknown): OutcomeCut {
  return OUTCOME_CUTS.some((c) => c.value === value) ? (value as OutcomeCut) : "variant";
}

const COHORT_SPEC: FilterSpec = {
  name: "cohort",
  label: "First orders in the last",
  kind: "choice",
  options: COHORTS.map((value) => ({ value: String(value), label: `${value} days` })),
};

const CUT_SPEC: FilterSpec = {
  name: "by",
  label: "Cut",
  kind: "choice",
  options: OUTCOME_CUTS,
};

export function OutcomesControls({
  controls,
  funnels,
  selected,
  cohort,
  by,
}: {
  controls: Controls;
  funnels: FunnelSummary[];
  selected: string;
  cohort: CohortDays;
  by: OutcomeCut;
}) {
  const funnelSpec: FilterSpec = {
    name: "funnel",
    label: "Funnel",
    kind: "choice",
    anyLabel: mostVisitedLabel(funnels),
    options: funnels.map((f) => ({ value: f.funnel_slug, label: funnelName(f.funnel_slug) })),
  };
  return (
    <div className="flex flex-wrap items-end gap-4">
      <FilterBar
        layout="segmented"
        filters={[COHORT_SPEC, CUT_SPEC]}
        values={{ ...controls.values, cohort: String(cohort), by }}
        onChange={controls.onChange}
      />
      <FilterBar
        layout="inline"
        filters={[funnelSpec]}
        values={{ ...controls.values, funnel: chosenFunnel(controls, selected) }}
        onChange={(next) => controls.onChange({ ...next, funnel: next.funnel ?? null })}
      />
    </div>
  );
}
